import { POWERUP, WORLD } from '../constants';
import { mulberry32, randRange } from '../utils/rng';

export type PowerupKind = 'surge' | 'shield' | 'double';

export interface Powerup {
  id: number;
  kind: PowerupKind;
  x: number;
  y: number;
  /** Bob phase offset so multiple powerups don't pulse in sync. */
  bobPhase: number;
  /** Time-of-spawn in sim seconds — drives the entrance scale animation. */
  spawnedAt: number;
}

/**
 * Manages the pool of active powerups in the world.
 *
 * Spawns are throttled by a poisson-like jittered interval and capped at
 * POWERUP.MAX_ACTIVE. Rarities are weighted (surge > double > shield).
 */
export class PowerupField {
  public readonly powerups: Powerup[] = [];
  private nextId = 1;
  private nextSpawnAt = 0;
  private readonly rng: () => number;

  constructor(seed: number = 0xBE5712) {
    this.rng = mulberry32(seed);
    // First spawn delayed a few seconds from game start.
    this.nextSpawnAt = 4 + this.rng() * 3;
  }

  /** Per-tick. Returns true if a new powerup was added this tick. */
  step(now: number, playerX: number, playerY: number): boolean {
    if (this.powerups.length >= POWERUP.MAX_ACTIVE) return false;
    if (now < this.nextSpawnAt) return false;
    this.spawn(playerX, playerY);
    // Schedule next attempt — ±50% jitter around the mean.
    this.nextSpawnAt = now + POWERUP.SPAWN_INTERVAL_MEAN * (0.5 + this.rng());
    return true;
  }

  /** Force-clear (used on respawn for a fresh field). */
  reset(now: number): void {
    this.powerups.length = 0;
    this.nextSpawnAt = now + 4 + this.rng() * 3;
  }

  remove(id: number): void {
    const idx = this.powerups.findIndex((p) => p.id === id);
    if (idx >= 0) this.powerups.splice(idx, 1);
  }

  private spawn(playerX: number, playerY: number): void {
    // Pick a location away from the player + away from world edges.
    const margin = WORLD.EDGE_PADDING * 3;
    let x = 0, y = 0;
    for (let attempt = 0; attempt < 8; attempt++) {
      x = randRange(this.rng, margin, WORLD.SIZE - margin);
      y = randRange(this.rng, margin, WORLD.SIZE - margin);
      const dx = x - playerX;
      const dy = y - playerY;
      if (dx * dx + dy * dy > 800 * 800) break;
    }
    // Weighted random kind.
    const roll = this.rng();
    let kind: PowerupKind;
    if (roll < POWERUP.W_SURGE) kind = 'surge';
    else if (roll < POWERUP.W_SURGE + POWERUP.W_DOUBLE) kind = 'double';
    else kind = 'shield';

    this.powerups.push({
      id: this.nextId++,
      kind,
      x, y,
      bobPhase: this.rng() * Math.PI * 2,
      spawnedAt: performance.now() * 0.001,
    });
  }
}
